import React from 'react';
import { CheckSquare, Square } from 'phosphor-react';
import {
  CreditApplicationFields,
  CreditApplicationFieldsValues,
  InternalSignatureDocumentSigner,
  renderCheckboxOptions,
  renderTemplate,
} from '@dill/dill-shared';
import { isHTML } from '../../../../utils/helpers';
import {
  CHECKBOX_STYLE_FIELD_TYPES,
  resolveCheckboxOrientation,
  resolveSalesRepFieldLabel,
  parseOptionList,
  getConditionalSubFieldDefsById,
  SupplierRefOption,
} from './conditionalOptionUtils';

// Lowercased, hyphenated placeholder stem for a signer's own {{<label>-signature}}/
// {{<label>-sign-date}} template keys — same slugification convention nextFieldId() in
// InternalSignatureDocumentsSection.tsx uses for generated field ids, just without the uniqueness suffix
// (this needs to be a *stable*, predictable key so a template author can actually write it).
const slugify = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

// Shared by both the templated renderer below and InternalSignatureDocumentPlainFieldList — maps
// each top-level field id to its display-ready value: CHECKBOX_STYLE_FIELD_TYPES (OPTION_FIELD/
// CHECKBOX_LIST/CONDITIONAL_SELECTED_FIELD) render as an options list with the picked one checked
// off, everything else is the raw saved value — e.g. a supplier-sales-reps DROPDOWN saving ERP ID
// shows the ERP ID as-is, same as its "Value to save" setting intends. Conditional sub-field values
// (fv.fieldValue) get merged in by their own id too, so a {{sub-field-id}} placeholder in a
// template still resolves.
const buildFieldValuesById = (
  fieldValues: CreditApplicationFieldsValues[],
  fields?: CreditApplicationFields[]
): Record<string, any> => {
  const fieldById = new Map((fields ?? []).map((f) => [f.id, f]));
  return fieldValues.reduce<Record<string, any>>((acc, fv) => {
    const fieldDef = fieldById.get(fv.id);
    if (fieldDef && CHECKBOX_STYLE_FIELD_TYPES.has(fv.fromField)) {
      acc[fv.id] = renderCheckboxOptions(
        parseOptionList(fieldDef.optionList),
        typeof fv.value === 'string' ? fv.value : '',
        resolveCheckboxOrientation(fieldDef),
        fieldDef.checkboxPosition ?? 'left'
      );
    } else {
      acc[fv.id] = fv.value;
    }
    (fv.fieldValue ?? []).forEach((sub) => {
      acc[sub.id] = sub.value;
    });
    return acc;
  }, {});
};

// Same {{field-id}} -> value mechanism as a credit-application field's Form Template (see
// DynamicForm.tsx) — plugs an internal signature document instance's collected fieldValues into its
// definition's HTML template. Extracted out of CreditApplicationDetails' InternalSignatureDocumentsCard so it
// can also be reused by the standalone SignInternalSignatureDocument page.
export const InternalSignatureDocumentTemplatePreview = ({
  formTemplate,
  fieldValues,
  fields,
  signers,
  extraValues,
}: {
  formTemplate?: string;
  fieldValues: CreditApplicationFieldsValues[];
  fields?: CreditApplicationFields[];
  signers?: InternalSignatureDocumentSigner[];
  extraValues?: Record<string, string>;
}) => {
  if (!formTemplate) return null;

  const valuesById = buildFieldValuesById(fieldValues, fields);

  (signers ?? []).forEach((signer) => {
    const label = signer.roleLabel || signer.role;
    const slug = slugify(label);
    if (!slug) return;
    valuesById[`${slug}-signature`] = signer.signature
      ? `<img src="${signer.signature}" alt="${label} signature" style="height:32px;width:auto;max-width:140px;object-fit:contain;vertical-align:middle;" />`
      : '';
    valuesById[`${slug}-sign-date`] = signer.dateSigned ?? '';
  });

  Object.entries(extraValues ?? {}).forEach(([key, value]) => {
    valuesById[key] = value;
  });

  const rendered = renderTemplate(formTemplate, valuesById);

  return isHTML(rendered) ? (
    <div
      className="text-sm border-t border-gray-100 pt-2"
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  ) : (
    <p className="text-sm border-t border-gray-100 pt-2">{rendered}</p>
  );
};

// One option in a checkbox-style list — a real square checkbox glyph (filled + checked when
// selected, empty outline otherwise) next to its label, same convention a fillable OPTION_FIELD/
// CHECKBOX_LIST/CONDITIONAL_SELECTED_FIELD uses when it's actually being filled in, just read-only
// here.
const CheckboxOption = ({ label, isChecked }: { label: string; isChecked: boolean }) => (
  <div className="flex items-center gap-1.5">
    {isChecked ? (
      <CheckSquare size={15} weight="fill" className="shrink-0 text-blue-600" />
    ) : (
      <Square size={15} className="shrink-0 text-gray-300" />
    )}
    <span className={`text-xs ${isChecked ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
      {label}
    </span>
  </div>
);

// A field's own answer, right below its label. TEXT/NUMBER/DATE/etc. are just the raw value;
// OPTION_FIELD/CHECKBOX_LIST/CONDITIONAL_SELECTED_FIELD (CHECKBOX_STYLE_FIELD_TYPES) list every
// option as a CheckboxOption, in a row or column per the field's own "Are options in a row or
// column" setting; CHECK_BOX is the same, just a single option using the field's own name.
const FieldAnswer = ({ field, value }: { field: CreditApplicationFields; value: any }) => {
  if (CHECKBOX_STYLE_FIELD_TYPES.has(field.fromField)) {
    const options = parseOptionList(field.optionList);
    if (options.length === 0) return <span className="text-xs text-gray-400">—</span>;
    const selected = typeof value === 'string' ? value : '';
    // resolveCheckboxOrientation, not a raw `field.isRow` check — isRow is inverted (true means
    // stack as a column) per every existing template's data, see its own doc comment.
    const isRow = resolveCheckboxOrientation(field) === 'row';
    return (
      <div className={`flex ${isRow ? 'flex-row flex-wrap gap-x-4 gap-y-1.5' : 'flex-col gap-1'}`}>
        {options.map((option) => (
          <CheckboxOption key={option} label={option} isChecked={option === selected} />
        ))}
      </div>
    );
  }

  if (field.fromField === 'CHECK_BOX') {
    return <CheckboxOption label="Yes" isChecked={value === true} />;
  }

  return (
    <span className="text-xs text-gray-900">
      {value === null || value === undefined || value === '' ? (
        <span className="text-gray-400">—</span>
      ) : (
        String(value)
      )}
    </span>
  );
};

// Fallback for when a document has no formTemplate (it's optional — see
// InternalSignatureDocumentsSection.tsx's Form Template help text) — without this, a document with
// no template renders nothing but its signer cards, with every field answer invisible even though
// it was filled in and saved. Lists each top-level field's name next to its value, in definition
// order, skipping INTERNAL_SIGNER (its own signer-card section below already covers that) and
// DOCUMENT_FIELD (static legal text, not an answer). A CONDITIONAL_SELECTED_FIELD also lists
// whichever nested sub-fields were collected under the picked option, indented underneath it —
// the same values a template's {{sub-field-id}} placeholder could reference, just without
// requiring one.
export const InternalSignatureDocumentPlainFieldList = ({
  fields,
  fieldValues,
  salesReps,
}: {
  fields?: CreditApplicationFields[];
  fieldValues: CreditApplicationFieldsValues[];
  salesReps?: SupplierRefOption[];
}) => {
  const visibleFields = (fields ?? []).filter(
    (f) => f.fromField !== 'INTERNAL_SIGNER' && f.fromField !== 'DOCUMENT_FIELD'
  );
  if (visibleFields.length === 0) return null;

  const fieldValueById = new Map(fieldValues.map((fv) => [fv.id, fv]));

  // Paired into rows of 2 (rather than one flat grid) so each row can carry its own full-width
  // divider — a CSS grid's implicit row lines don't span cleanly across columns of uneven height.
  const rows: CreditApplicationFields[][] = [];
  for (let i = 0; i < visibleFields.length; i += 2) {
    rows.push(visibleFields.slice(i, i + 2));
  }

  return (
    <div className="flex flex-col border-t border-gray-100">
      {rows.map((row, rowIndex) => (
        <div
          key={row[0].id}
          className={`grid grid-cols-1 gap-x-6 gap-y-4 py-3 sm:grid-cols-2 ${
            rowIndex !== rows.length - 1 ? 'border-b border-gray-100' : ''
          }`}>
          {row.map((field) => {
            const value = fieldValueById.get(field.id)?.value;
            const subValues = (fieldValueById.get(field.id)?.fieldValue ?? []).filter(
              (sub) => !sub.hidden
            );
            const subFieldDefsById = getConditionalSubFieldDefsById(field);
            return (
              <div key={field.id} className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-500">
                  {resolveSalesRepFieldLabel(field, value, salesReps) || field.id}
                </span>
                <FieldAnswer field={field} value={value} />
                {subValues.length > 0 && (
                  <div className="ml-1 mt-1 flex flex-col gap-1.5 border-l-2 border-gray-100 pl-2.5">
                    {subValues.map((sub) => {
                      const subFieldDef = subFieldDefsById.get(sub.id);
                      const subLabel = subFieldDef
                        ? resolveSalesRepFieldLabel(subFieldDef, sub.value, salesReps)
                        : sub.name;
                      return (
                        <div key={sub.uniqueId} className="flex flex-col gap-0.5">
                          <span className="text-[11px] font-medium text-gray-400">{subLabel}</span>
                          <span className="text-xs text-gray-700">
                            {sub.value === null || sub.value === undefined || sub.value === ''
                              ? '—'
                              : String(sub.value)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
